import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { listarPresupuestos } from "../api"
import {
  formatCurrency, formatNumber, formatFecha,
  estadoConfig, periodicidadLabel,
  ANIOS_DISPONIBLES, ESTADO_CONFIG,
  muestraMonto, muestraUnidades,
} from "../helpers"
import DrawerDetalle from "./DrawerDetalle"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Presupuestos", link: "#" },
  { title: "Mis presupuestos", link: "#" },
]

const ahora = new Date()

const ListaPresupuestos = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [anio, setAnio] = useState(ahora.getFullYear())
  const [estado, setEstado] = useState("")
  const [sucursalFilter, setSucursalFilter] = useState("") // "" = todas, "null" = sólo nivel empresa

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [presupuestoActivo, setPresupuestoActivo] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaId = empresaSeleccionada?.id || empresaSeleccionada?.idempresa || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Presupuestos | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Presupuestos"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  useEffect(() => {
    setItems([])
    setError("")
  }, [empresaId])

  const cargar = useCallback(async () => {
    if (!empresaId) return
    setLoading(true)
    setError("")
    try {
      const filtros = { empresaId, anio }
      if (estado) filtros.estado = estado
      if (sucursalFilter === "null") filtros.sucursalId = "null"
      const r = await listarPresupuestos(filtros)
      const body = r.data
      if (body?.success === false) throw new Error(body.message || "Error en el servidor")
      setItems(body?.data || [])
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el listado.")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [empresaId, anio, estado, sucursalFilter])

  useEffect(() => { cargar() }, [cargar, refreshKey])

  const handleAbrirDetalle = (id) => {
    setPresupuestoActivo(id)
    setDrawerAbierto(true)
  }

  const handleCambioDrawer = (nuevoId) => {
    setRefreshKey(k => k + 1)
    if (typeof nuevoId === "string") {
      // Cambiar al nuevo presupuesto (cuando se clona)
      setPresupuestoActivo(nuevoId)
    }
  }

  // Estadísticas por estado (chips arriba)
  const estadisticas = useMemo(() => {
    const stats = {}
    Object.keys(ESTADO_CONFIG).forEach(k => { stats[k] = 0 })
    items.forEach(p => { if (p.estado) stats[p.estado] = (stats[p.estado] || 0) + 1 })
    return stats
  }, [items])

  if (!empresaId) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> arriba a la derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Presupuestos
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>Mis presupuestos</h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong>{nitemisor && ` · NIT ${nitemisor}`}
            </div>
          </div>
          <Link to="/portal/presupuestos/nuevo" className="btn btn-primary">
            <i className="mdi mdi-plus me-1" /> Nuevo presupuesto
          </Link>
        </div>

        {/* Filtros */}
        <div className="d-flex flex-wrap align-items-end gap-2" style={{ gap: 8 }}>
          <div>
            <div style={labelStyle}>Año</div>
            <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={inputStyle}>
              {ANIOS_DISPONIBLES.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Estado</div>
            <select value={estado} onChange={e => setEstado(e.target.value)} style={inputStyle}>
              <option value="">Todos</option>
              {Object.entries(ESTADO_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Alcance</div>
            <select value={sucursalFilter} onChange={e => setSucursalFilter(e.target.value)} style={inputStyle}>
              <option value="">Todos (empresa + sucursales)</option>
              <option value="null">Solo nivel empresa</option>
            </select>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary"
            onClick={() => setRefreshKey(k => k + 1)} disabled={loading}
            style={{ height: 34 }}>
            <i className="mdi mdi-refresh me-1" /> Refrescar
          </button>
        </div>
      </div>

      {/* Chips de estado */}
      {!loading && items.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.entries(ESTADO_CONFIG).map(([k, v]) => {
            const count = estadisticas[k] || 0
            if (count === 0) return null
            const activo = estado === k
            return (
              <button key={k} type="button"
                onClick={() => setEstado(activo ? "" : k)}
                style={{
                  background: activo ? v.color : v.bg,
                  color: activo ? "#fff" : v.color,
                  border: activo ? `1px solid ${v.color}` : "1px solid transparent",
                  padding: "5px 13px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                <i className={`mdi ${v.icon}`} />
                {v.label}
                <span style={{
                  background: activo ? "rgba(255,255,255,0.2)" : `${v.color}30`,
                  color: activo ? "#fff" : v.color,
                  padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                }}>{count}</span>
              </button>
            )
          })}
        </div>
      )}

      {error && (
        <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10 }}>
          <i className="mdi mdi-alert-circle me-1" /> {error}
          <button type="button" className="btn btn-sm btn-outline-danger ms-3" onClick={cargar}>Reintentar</button>
        </div>
      )}

      {loading && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando presupuestos…</div>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-file-chart-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>No hay presupuestos {estado ? `en estado ${estadoConfig(estado).label}` : ""} para {anio}</h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 16px", fontSize: 14 }}>
            Crea el primer presupuesto del año para empezar a planificar las ventas.
          </p>
          <Link to="/portal/presupuestos/nuevo" className="btn btn-primary">
            <i className="mdi mdi-plus me-1" /> Crear presupuesto
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Nombre</th>
                  <th style={th}>Alcance</th>
                  <th style={th}>Periodicidad</th>
                  <th style={{ ...th, textAlign: "right" }}>Monto</th>
                  <th style={{ ...th, textAlign: "right" }}>Unidades</th>
                  <th style={{ ...th, textAlign: "center" }}>Periodos</th>
                  <th style={{ ...th, textAlign: "center" }}>v</th>
                  <th style={{ ...th, textAlign: "center" }}>Estado</th>
                  <th style={th}>Vigencia</th>
                  <th style={{ ...th, textAlign: "center", width: 70 }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p, i) => {
                  const cfg = estadoConfig(p.estado)
                  return (
                    <tr key={p.id} style={{ background: i % 2 ? "#fafbfc" : "#fff" }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{p.nombre}</div>
                        {p.descripcion && <div style={{ fontSize: 11, color: "#94a3b8" }}>{p.descripcion}</div>}
                      </td>
                      <td style={td}>
                        {p.sucursal ? (
                          <span><i className="mdi mdi-source-branch me-1 text-secondary" />{p.sucursal.nombre}</span>
                        ) : (
                          <span style={{ color: "#1e40af", fontWeight: 600 }}>
                            <i className="mdi mdi-domain me-1" />Empresa
                          </span>
                        )}
                      </td>
                      <td style={td}>{periodicidadLabel(p.periodicidad)}</td>
                      <td style={{ ...td, textAlign: "right", color: "#10b981", fontWeight: 700 }}>
                        {muestraMonto(p.tipoMedicion) ? formatCurrency(p.montoTotal, p.moneda) : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: "#3b82f6", fontWeight: 600 }}>
                        {muestraUnidades(p.tipoMedicion) ? formatNumber(p.unidadesTotal, 0) : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>{p._count?.periodos ?? "—"}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <span style={{ background: "#eff6ff", color: "#1e40af", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                          v{p.version}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <span style={{
                          padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: cfg.bg, color: cfg.color,
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                          <i className={`mdi ${cfg.icon}`} />
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>
                        {formatFecha(p.fechaInicio)} — {formatFecha(p.fechaFin)}
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <button type="button" className="btn btn-sm btn-outline-primary"
                          onClick={() => handleAbrirDetalle(p.id)} title="Ver detalle">
                          <i className="mdi mdi-eye-outline" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DrawerDetalle
        abierto={drawerAbierto}
        presupuestoId={presupuestoActivo}
        onCerrar={() => setDrawerAbierto(false)}
        onCambio={handleCambioDrawer}
      />
    </div>
  )
}

const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }
const inputStyle = { fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34, minWidth: 130 }
const th = { background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }
const td = { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#0f172a", verticalAlign: "middle" }

ListaPresupuestos.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ListaPresupuestos)
